import { Box, Container, Button } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { IProgress, ISessionProps, IntegrationPathType } from "../../../main/components/IProgress";
import { Typography, Note } from "@appsflyer/fe-ui-core";
import transition from "../../../main/components/transition";
import { amplitudeTrack_continue } from "../../../services/utils";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { DJSimpleCodeBlock } from "../../../main/components/codeblock";
import { useState } from "react";

const InstallSDK_step6 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClick = () => {
    const progressArr: IProgress[] = location.state?.progress.slice();
    
    // Add "Start SDK" progress item (previously done by step7)
    progressArr.push({
      id: "reactnative_startSDK",
      text: "progress_startsdk_startSDK",
      order: location.state?.progress.length + 1,
      startTime: new Date(),
      updateTime: new Date(),
    });

    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
      path: location.state?.path || IntegrationPathType.StartSDK,
    };

    amplitudeTrack_continue(progressState);
    // Navigate directly to StartSDK (step7 was removed)
    navigate(`/startsdk/reactnative_android/startsdk`, {
      state: progressState,
    });
  };

  const steps = [
    {
      label: t("StartSDK_Step6_reactnativeandroid_Solution_description3"),
      component: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <Trans 
                i18nKey="StartSDK_Step6_reactnativeandroid_Solution_description4"
                components={{ code: <code /> }}
          />
          </Typography>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("StartSDK_Step6_reactnativeandroid_Solution_description5"),
      component: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <Trans 
                i18nKey="StartSDK_Step6_reactnativeandroid_Solution_description6"
                components={{ code: <code /> }}
          />
          </Typography>
          <DJSimpleCodeBlock
            codePrimary={`// withCustomAndroidManifest.js
const { withAndroidManifest } = require('@expo/config-plugins');

module.exports = function withCustomAndroidManifest(config) {
  return withAndroidManifest(config, async (config) => {
    const androidManifest = config.modResults;
    const manifest = androidManifest.manifest;
    
    // Ensure xmlns:tools is present in the <manifest> tag
    if (!manifest.$['xmlns:tools']) {
      manifest.$['xmlns:tools'] = 'http://schemas.android.com/tools';
    }

    const application = manifest.application[0];

    // Add tools:replace attribute for dataExtractionRules and fullBackupContent
    application['$']['tools:replace'] = 'android:dataExtractionRules, android:fullBackupContent';

    // Set dataExtractionRules and fullBackupContent as attributes within <application>
    application['$']['android:dataExtractionRules'] = '@xml/secure_store_data_extraction_rules';
    application['$']['android:fullBackupContent'] = '@xml/secure_store_backup_rules';

    return config;
  });
};`}
            language="javascript"
            showLineNumbers={false}
          />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("StartSDK_Step6_reactnativeandroid_Solution_description7"),
      component: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("StartSDK_Step6_reactnativeandroid_Solution_description8")}
          </Typography>
          <DJSimpleCodeBlock
            codePrimary={`// app.json
{
  "expo": {
    // ... other configurations ...
    "plugins": [
      "./withCustomAndroidManifest.js",
      [
        "react-native-appsflyer",
        {
          "shouldUseStrictMode": true
        }
      ]
    ]
  }
}`}
            language="json"
            showLineNumbers={false}
          />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Box sx={{ mb: 4 }} display={"flex"} justifyContent={"space-between"}>
          <Typography variant="h1">
            {t("StartSDK_Step6_reactnativeandroid_title")}
          </Typography>
        </Box>
        {/* First two steps as regular content */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ mb: 2, maxWidth: "800px" }}>
            {t("StartSDK_Step6_reactnativeandroid_Conflict_title")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, maxWidth: "800px" }}>
          <Trans 
                i18nKey="StartSDK_Step6_reactnativeandroid_Conflict_description"
                components={{ code: <code /> }}
          />
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h3" sx={{ mb: 2, maxWidth: "800px" }}>
            {t("StartSDK_Step6_reactnativeandroid_Solution_title")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, maxWidth: "800px" }}>
            <Trans 
                i18nKey="StartSDK_Step6_reactnativeandroid_Solution_description"
                components={{ code: <code /> }}
          />
          </Typography>
          <Typography>
            {t("StartSDK_Step6_reactnativeandroid_Solution_description2")}
          </Typography>
        </Box>
        <Stepper
          activeStep={activeStep}
          style={{ border: "none" }}
          orientation="vertical"
        >
          {steps.map(({ label, component, shouldDisplayNavigationButtons }) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {component}
                {shouldDisplayNavigationButtons && (
                  <Box sx={{ mt: 2 }}>
                    {activeStep !== 0 && (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleBack}
                      >
                        {t("general_Back")}
                      </Button>
                    )}
                    {activeStep !== steps.length - 1 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        {t("general_Next")}
                      </Button>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
        {/* Result section */}
        <Note title="" sx={{ mt: 2 }}>
          <Trans
                i18nKey="StartSDK_Step6_reactnativeandroid_Result"
                components={{ code: <code /> }}
          />
        </Note>{" "}
      </Box>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
        width={"100%"}
      >
        <Button
          variant="outlined"
          color="secondary"
          size="medium"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleClick}
        >
          {t("general_Next")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(InstallSDK_step6);
